import { gql, useQuery } from '@apollo/client';
import { LoadingSpinner } from 'common';
import { Alert } from 'evergreen-ui';
import { FeatureFlagContext, FeatureFlagMap, ProviderPortalFeatureFlagDto } from 'featureflag';
import { setCurrentUserForSentry } from 'monitoring';
import React, { useContext, useMemo, useState, useEffect } from 'react';
import { CurrentUserData, GlobalSettingData } from 'security';


export const CurrentUserContext = React.createContext<CurrentUserData | null>(null);
export const useCurrentUser = () => useContext(CurrentUserContext);

export const GlobalSettingContext = React.createContext<GlobalSettingData[] | null>(null);
export const useGlobalSetting = () => useContext(GlobalSettingContext);


const CURRENT_USER_QUERY = gql`
query CurrentUser {
  currentUser {
    guid
    username
    firstName
    lastName
    email
    externalId
    role
    canManageProviderUser
    canManageClientReport
    canAccessClientPlacement
    canAccessClientReport
    canAccessProviderMetrics
    canAccessRoster
    canAccessAuditLog
    canAccessContactEdit
    canAccessContactRead
    isProviderSupport
    enableOfferHours
    provider {
      id
      guid
      name
    }  
  }

  globalSettings {
    guid
    settingKey
    settingValue
  }

  providerPortalFeatureFlags {
    key
    enabled
    description
  }
}
`;

const networkErrorFriendlyMessage = 'Looks like the server is taking too long to respond, please try again later.';

type CurrentUserQueryResult = {
    currentUser: CurrentUserData,
    globalSettings: GlobalSettingData[],
    providerPortalFeatureFlags: ProviderPortalFeatureFlagDto[],
};

export const CurrentUser: React.FC = ({ children }) => {
    const [hasNetworkErr, setHasNetworkErr] = useState(false);
    const { loading, data, error } = useQuery<CurrentUserQueryResult, {}>(
        CURRENT_USER_QUERY, {
        onError: ({ networkError }) => {
            if (networkError !== null) {
                setHasNetworkErr(true);
            }
        }
    }
    );

    const currentUser = data?.currentUser ?? null;
    useEffect(() => {
        setCurrentUserForSentry(currentUser);
    }, [currentUser]);

    const featureFlagContextValue = useMemo(() => {
        const flags: FeatureFlagMap = {};
        data?.providerPortalFeatureFlags?.forEach((flag) => {
            flags[flag.key] = flag.enabled;
        });
        return { isLoading: loading, flags };
    }, [data, loading]);

    if (loading) {
        return <LoadingSpinner spacing={20} centerScreen />;
    }
    if (error) {
        return (
            <div className='items-stretch h-screen flex'>
                <Alert margin='auto' alignSelf='center' intent='danger' title={
                    hasNetworkErr ? networkErrorFriendlyMessage : error.message
                } />
            </div>
        );
    }

    if (!data?.currentUser) {
        return null;
    }
    return (
        <CurrentUserContext.Provider value={data.currentUser}>
            <GlobalSettingContext.Provider value={data.globalSettings}>
                <FeatureFlagContext.Provider value={featureFlagContextValue}>
                    {children}
                </FeatureFlagContext.Provider>
            </GlobalSettingContext.Provider>
        </CurrentUserContext.Provider>
    );
};
